import { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { EntityCounts } from '@/types';

interface EntityCountCardProps {
  title: string;
  counts: EntityCounts;
  icon?: LucideIcon;
  className?: string;
}

/**
 * Card component that displays entity counts split between CRM and Xero
 * Shows synced count and unsynced count for each system
 */
export function EntityCountCard({
  title,
  counts,
  icon: Icon,
  className,
}: EntityCountCardProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center space-x-2">
          {Icon && <Icon className="h-5 w-5 text-gray-600" />}
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
      </div>

      {/* Content - Split into two columns */}
      <div className="grid grid-cols-2 divide-x divide-gray-100">
        {/* CRM Column */}
        <div className="px-4 py-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            CRM
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {counts.highlevel.total}
              </p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-600">
                {counts.highlevel.synced} synced
              </span>
            </div>
            {counts.highlevel.unsynced > 0 && (
              <div className="flex items-center text-sm">
                <span className="text-amber-600">
                  {counts.highlevel.unsynced} not in Xero
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Xero Column */}
        <div className="px-4 py-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Xero
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {counts.xero.total}
              </p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-600">
                {counts.xero.synced} synced
              </span>
            </div>
            {counts.xero.unsynced > 0 && (
              <div className="flex items-center text-sm">
                <span className="text-amber-600">
                  {counts.xero.unsynced} not in CRM
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
